import { createTheme } from '@mui/material/styles';
import { ResolvedTokens, alpha, mix } from '@porter/shared-theme';
import { SURFACE_DEFAULTS } from './surface';

// Augment the palette to include an porterGrey color
declare module '@mui/material/styles' {
  interface Palette {
    accent: Palette['primary'];
    darkGrey: Palette['primary'];
    medGrey: Palette['primary'];
    lightGrey: Palette['primary'];
    outlineGrey: Palette['primary'];
    lightOutlineGrey: Palette['primary'];
  }

  interface PaletteOptions {
    accent?: PaletteOptions['primary'];
    darkGrey?: PaletteOptions['primary'];
    medGrey?: PaletteOptions['primary'];
    lightGrey?: PaletteOptions['primary'];
    outlineGrey?: PaletteOptions['primary'];
    lightOutlineGrey?: PaletteOptions['primary'];
  }
}

// Update the Button's color options to include an porterGrey option
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    accent: true;
    darkGrey: true;
    medGrey: true;
    lightGrey: true;
    outlineGrey: true;
    lightOutlineGrey: true;
  }
  interface ButtonPropsVariantOverrides {
    smallLink: true;
    link: true;
    light: true;
    base: true;
  }
}

/**
 * The base variants below are assigned rather than merged, so they carry no
 * lineHeight and fall back to CSS `normal` — which each font computes from its
 * own metrics. Setting them explicitly keeps spacing identical whether a page
 * is themed or not, and stops a display face from resizing every heading.
 */
const HEADING_LINE_HEIGHT = 1.2;
const BODY_LINE_HEIGHT = 1.5;

const fontFamily = [
  'Montserrat',
  'Open Sans',
  'Helvetica',
  'Arial',
  'sans-serif',
];

const theme = createTheme({
  typography: {
    fontFamily: fontFamily.join(','),
  },
  palette: {
    // Same value the CSS token carries, so `divider` and surface('divider')
    // cannot drift apart on an unthemed page.
    divider: SURFACE_DEFAULTS['--surface-divider'],
    primary: {
      main: '#FF3008',
      light: '#FFD6CE',
    },
    secondary: {
      main: '#00A86B',
    },
    accent: {
      main: '#CEEEE3',
      contrastText: '#000000',
    },
    darkGrey: {
      main: '#333333',
      contrastText: '#FFFFFF',
    },
    medGrey: {
      main: '#666666',
      contrastText: '#FFFFFF',
    },
    outlineGrey: {
      main: '#939393',
      contrastText: '#FFFFFF',
    },
    lightGrey: {
      main: '#F5F5F5',
      contrastText: '#000000',
    },
    lightOutlineGrey: {
      main: '#bdbdbd',
      contrastText: '#000000',
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 5, // touches the outer wrapper
          '& .MuiOutlinedInput-notchedOutline': {
            // touches the actual outline
            borderRadius: 5,
          },
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: {},
          style: {
            fontFamily: fontFamily.join(','),
          },
        },
        {
          props: { variant: 'base' },
          style: ({ theme: currentTheme }) => ({
            color: '#ffffff',
            backgroundColor: currentTheme.palette.primary.main,
            fontSize: '0.875rem',
            fontWeight: '500',
            padding: '0.75rem 1rem',
            borderRadius: '10px',
            border: 'none',
            ':disabled': {
              opacity: 0.8,
              color: currentTheme.palette.lightGrey.main,
            },
            ':hover': {
              backgroundColor: '#c30000',
            },
          }),
        },
        {
          props: { variant: 'smallLink' },
          style: ({ theme: currentTheme }) => ({
            color: currentTheme.palette.darkGrey.main,
            fontSize: currentTheme.typography.body2.fontSize,
            fontWeight: '400',
            textDecoration: 'underline',
            padding: 0,
            ':hover': {
              backgroundColor: 'transparent',
            },
          }),
        },
        {
          props: { variant: 'link' },
          style: {
            color: '#000000',
            fontSize: '0.875rem',
            fontWeight: '700',
            textDecoration: 'underline',
            padding: 0,
            ':hover': {
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'light' },
          style: ({ theme: currentTheme }) => ({
            color: currentTheme.palette.darkGrey.main,
            fontSize: '0.875rem',
            fontWeight: '500',
            padding: '0.75rem 1rem',
            borderRadius: '10px',
            border: `1px solid ${currentTheme.palette.lightOutlineGrey.main}`,
            ':disabled': {
              backgroundColor: currentTheme.palette.lightGrey.main,
              color: currentTheme.palette.medGrey.main,
              opacity: 0.8,
            },
          }),
        },
        {
          props: { variant: 'light', color: 'secondary' },
          style: ({ theme: currentTheme }) => ({
            border: 'none',
            backgroundColor: currentTheme.palette.accent.main,
            color: currentTheme.palette.secondary.main,
            ':hover': {
              backgroundColor: currentTheme.palette.accent.main,
              filter: 'brightness(0.95)',
            },
            ':disabled': {
              backgroundColor: currentTheme.palette.accent.main,
              color: currentTheme.palette.secondary.main,
              opacity: 0.8,
            },
          }),
        },
        {
          props: { variant: 'light', color: 'primary' },
          style: ({ theme: currentTheme }) => ({
            border: 'none',
            backgroundColor: currentTheme.palette.primary.light,
            color: currentTheme.palette.primary.main,
            ':hover': {
              backgroundColor: currentTheme.palette.primary.light,
              filter: 'brightness(0.96)',
            },
            ':disabled': {
              backgroundColor: currentTheme.palette.primary.light,
              color: currentTheme.palette.primary.main,
              opacity: 0.8,
            },
          }),
        },
        {
          props: { variant: 'light', color: 'info' },
          style: ({ theme: currentTheme }) => ({
            border: 'none',
            backgroundColor: currentTheme.palette.lightGrey.main,
            color: currentTheme.palette.darkGrey.main,
            ':hover': {
              backgroundColor: currentTheme.palette.lightGrey.main,
              filter: 'brightness(0.95)',
            },
            ':disabled': {
              backgroundColor: currentTheme.palette.lightGrey.main,
              color: currentTheme.palette.darkGrey.main,
              opacity: 0.8,
            },
          }),
        },
      ],
    },
  },
});

theme.typography.h1 = {
  lineHeight: HEADING_LINE_HEIGHT,
  fontSize: '1.625rem',
  fontWeight: '700',
  textAlign: 'left',
};

theme.typography.h2 = {
  lineHeight: HEADING_LINE_HEIGHT,
  fontSize: '1.375rem',
  fontWeight: '700',
  textAlign: 'left',
  color: '#333333',
};

theme.typography.h3 = {
  lineHeight: HEADING_LINE_HEIGHT,
  fontSize: '1rem',
  fontWeight: '700',
  textAlign: 'left',
  color: '#333333',
};

theme.typography.h4 = {
  lineHeight: HEADING_LINE_HEIGHT,
  fontSize: '.6875rem',
  fontWeight: '700',
  textAlign: 'left',
  color: '#333333',
  textTransform: 'uppercase',
};

theme.typography.h5 = {
  lineHeight: HEADING_LINE_HEIGHT,
  fontSize: '.9rem',
  fontWeight: '600',
  textAlign: 'left',
  color: '#333333',
};

theme.typography.button = {
  fontSize: '1rem',
  fontWeight: '700',
  // Button labels are single-line, so this tracks what `normal` resolved to
  // before. Body leading here would grow every button in the app.
  lineHeight: HEADING_LINE_HEIGHT,
};

theme.typography.body1 = {
  lineHeight: BODY_LINE_HEIGHT,
  fontSize: '1.125rem',
  fontWeight: '400',
  textAlign: 'left',
  color: '#333333',
};

theme.typography.body2 = {
  lineHeight: BODY_LINE_HEIGHT,
  fontSize: '.875rem',
  fontWeight: '400',
  textAlign: 'left',
  color: '#333333',
};

export default theme;

/**
 * Booking site pages nest this below the default theme so the shop's colours
 * apply to their subtree only. Body and heading text come from the tokens, so
 * the derived contrast rules hold wherever MUI paints.
 */
/**
 * Opaque blend toward the foreground. Menus and dialogs float above the page,
 * so they need a real colour — and MUI runs palette values through
 * colorManipulator, which rejects color-mix() and CSS variables.
 */
export function createThemeFromTokens(tokens: ResolvedTokens) {
  // Dropped from the spread below so createPalette can supply the mode's own.
  // Kept, they win the merge and a dark page gets light-mode ripples and hover
  // overlays — a near-black wash on a near-black surface.
  const { action, grey, ...basePalette } = theme.palette;
  const isDark = tokens.fg === '#FFFFFF';
  // Same value as --surface-raised, so a MUI panel and a CSS one match.
  const raised = mix(tokens.base, tokens.fg, isDark ? 0.08 : 0.06);

  const themed = createTheme({
    ...theme,
    typography: {
      ...theme.typography,
      fontFamily: tokens.bodyFont,
    },
    palette: {
      ...basePalette,
      // MUI derives input borders, dividers and hover overlays from this. Left
      // on 'light' it paints rgba(0,0,0,.23) hairlines, invisible on a dark
      // theme. tokens.fg already tells us which side of the line we are on.
      mode: tokens.fg === '#FFFFFF' ? 'dark' : 'light',
      primary: { main: tokens.primary, contrastText: tokens.onPrimary },
      // The grey ramp is Porter's light-mode ink. Spread unchanged it paints
      // #333 body copy and #bdbdbd hairlines onto a black page, so every
      // `darkGrey.main` label in the scheduler and waitlist went dark on dark.
      darkGrey: { main: tokens.fg, contrastText: tokens.base },
      medGrey: { main: tokens.dim, contrastText: tokens.base },
      outlineGrey: { main: tokens.dim, contrastText: tokens.base },
      lightGrey: { main: raised, contrastText: tokens.fg },
      lightOutlineGrey: { main: tokens.line, contrastText: tokens.fg },
      // Left at the base mint, this leaked #CEEEE3 into progress lines, chips
      // and highlights on every themed page.
      accent: { main: tokens.accent, contrastText: tokens.base },
      text: { primary: tokens.fg, secondary: tokens.dim },
      divider: tokens.line,
      // default stays transparent so page containers show the themed
      // background, but paper must be opaque: a Select menu or dialog floating
      // over content with no background is unreadable.
      background: {
        default: 'transparent',
        paper: mix(tokens.base, tokens.fg, 0.08),
      },
    },
    components: {
      ...theme.components,
      MuiButton: {
        variants: [
          ...(theme.components?.MuiButton?.variants ?? []),
          {
            // Appended last so it beats the unthemed entry, which hardcodes a
            // white label and a Porter-red hover — both wrong under any other
            // primary. Rule 6.6 ships the label with the swatch.
            props: { variant: 'base' },
            style: {
              color: tokens.onPrimary,
              backgroundColor: tokens.primary,
              border: tokens.primaryEdge,
              ':hover': {
                backgroundColor: tokens.primary,
                border: tokens.primaryEdge,
                filter: 'brightness(0.92)',
              },
              // The unthemed entry dims the label to the grey ramp, which is a
              // page colour once themed — matches button.primary.disabled.
              ':disabled': {
                backgroundColor: raised,
                border: `1px solid ${tokens.line}`,
                color: tokens.dim,
                opacity: 1,
              },
            },
          },
          {
            // color defaults to 'primary', so every bare variant="light" lands
            // on the unthemed entry's primary.light fill — which MUI derives as
            // lighten(main, 0.2) once the palette is themed, near enough to the
            // primary.main label to read as one block. Rule 6.4 has already
            // fitted the accent to 4.5:1, so the tint pair comes from there.
            props: { variant: 'light', color: 'primary' },
            style: {
              backgroundColor: alpha(tokens.accent, 0.18),
              color: tokens.accent,
              ':hover': {
                backgroundColor: alpha(tokens.accent, 0.18),
                filter: 'brightness(0.96)',
              },
              ':disabled': {
                backgroundColor: alpha(tokens.accent, 0.18),
                color: tokens.accent,
                opacity: 0.8,
              },
            },
          },
          {
            // Same trap: the unthemed entry pairs the mint accent fill with the
            // fixed Porter green label, and only the fill follows the theme.
            props: { variant: 'light', color: 'secondary' },
            style: {
              backgroundColor: alpha(tokens.accent, 0.18),
              color: tokens.accent,
              ':hover': {
                backgroundColor: alpha(tokens.accent, 0.18),
                filter: 'brightness(0.96)',
              },
              ':disabled': {
                backgroundColor: alpha(tokens.accent, 0.18),
                color: tokens.accent,
                opacity: 0.8,
              },
            },
          },
          {
            // Primary may sit on the page colour; link text is the accent's job (6.4).
            props: { variant: 'text', color: 'primary' },
            style: {
              color: tokens.accent,
              ':hover': { backgroundColor: alpha(tokens.accent, 0.08) },
            },
          },
          {
            // The unthemed entry hardcodes black.
            props: { variant: 'link' },
            style: { color: tokens.fg },
          },
        ],
        styleOverrides: {
          // Rule 6.7. A fill within 3:1 of the page has no shape without an
          // edge. The CSS-class buttons read --surface-primary-edge; MUI's own
          // need it here.
          contained: { border: tokens.primaryEdge },
        },
      },
      MuiLink: {
        variants: [
          {
            // Link defaults to primary, the same trap as a bare text button.
            props: { color: 'primary' },
            style: {
              color: tokens.accent,
              '--Link-underlineColor': alpha(tokens.accent, 0.4),
            },
          },
        ],
      },
      // Spec 5 — selection, highlights and indicators are the accent's job, and
      // rule 6.4 has already fitted it to 4.5:1. Primary is a fill colour and
      // may legitimately sit within 3:1 of the background, so MUI's defaults
      // paint these invisible on, say, a black primary over a black page.
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: tokens.accent },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            color: tokens.dim,
            '&.Mui-selected': { color: tokens.accent },
          },
        },
      },
      MuiRadio: {
        styleOverrides: { root: { '&.Mui-checked': { color: tokens.accent } } },
      },
      MuiCheckbox: {
        styleOverrides: { root: { '&.Mui-checked': { color: tokens.accent } } },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            '&.MuiCircularProgress-colorPrimary': { color: tokens.accent },
          },
        },
      },
    },
  });

  // lineHeight comes from the base variants, so themed and unthemed pages keep
  // identical spacing. h6 is untouched by the base, hence the explicit value.
  (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const).forEach(variant => {
    themed.typography[variant] = {
      ...theme.typography[variant],
      fontFamily: tokens.headFont,
      color: tokens.heading,
      lineHeight: HEADING_LINE_HEIGHT,
    };
  });

  themed.typography.body1 = { ...theme.typography.body1, color: tokens.fg };
  themed.typography.body2 = { ...theme.typography.body2, color: tokens.dim };
  // createTheme re-merges typography against MUI's own defaults, and the default
  // `button` variant carries textTransform: 'uppercase'. The base drops it by
  // assigning the variant outright; without the same assignment here, Tab and
  // Button — both of which spread this variant — render all caps once a shop
  // picks any theme.
  themed.typography.button = { ...theme.typography.button };

  return themed;
}
